import React from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  Tooltip,
  useTheme,
  useMediaQuery,
  TextField,
  ListSubheader,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  DropDownArrowIcon,
  RequireIcon as CustomRequireIcon,
} from "@ippon/shared-assets";
import { CustomTruncateText } from "@ippon/shared-components";
import "./CustomSingleSelect.css";
import { Controller } from "react-hook-form";
import { useResponsiveMenuPlacement } from "../../../hooks/useResponsiveMenuPlacement";

/**
 * react-hook-form の errors はネストフィールド ("a.b.c") を入れ子オブジェクトで返すため、
 * ドット記法の name でも辿れるようにする。
 */
const resolveError = (errors: any, name: string): any => {
  if (!errors || !name) return undefined;
  if (!name.includes(".")) return errors[name];
  return name
    .split(".")
    .reduce<any>((acc, key) => (acc == null ? undefined : acc[key]), errors);
};

export interface CustomSingleSelectProps {
  label: string;
  name: string;
  placeholder?: string;
  control?: any;
  errors?: any;
  disabled?: boolean;
  readonly?: boolean;
  isRequired?: boolean;
  extraClassName?: string;
  isShowInColumn?: boolean;
  options?: Array<{ value: string | number; label: string }>;
  labelWidth?: string;
  labelWidthSp?: string;
  inputWidth?: string;
  inputWidthSp?: string;
  onChangeValue?: (val: any) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  defaultLimit?: number;
  /** 開いた選択肢メニューを最長ラベルの幅まで広げる。トリガー本体の幅は変えない。既定 true。 */
  autoMenuWidth?: boolean;
  hasSpacing?: boolean;
  labelTooltip?: string;
  /** ラベル文字列の右隣に挟む任意の React ノード (AI バッジ等) */
  labelSuffix?: React.ReactNode;
}

const DEFAULT_MENU_MAX_HEIGHT = 360;

const CustomSingleSelect: React.FC<CustomSingleSelectProps> = ({
  label,
  name,
  placeholder,
  control,
  errors = {},
  disabled = false,
  readonly = false,
  isRequired = false,
  extraClassName = "flexRow",
  isShowInColumn = false,
  options = [],
  labelWidth = "98px",
  labelWidthSp: _labelWidthSp = "100px",
  inputWidth = "100%",
  inputWidthSp = "100%",
  onChangeValue,
  searchable = false,
  searchPlaceholder = "",
  defaultLimit = 50,
  autoMenuWidth = true,
  hasSpacing,
  labelTooltip,
  labelSuffix,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fieldError = resolveError(errors, name);

  const [searchText, setSearchText] = React.useState<string>("");
  const [menuMinWidth, setMenuMinWidth] = React.useState<number>(0);
  const {
    menuMaxHeight,
    menuAnchorOrigin,
    menuTransformOrigin,
    updateMenuPlacement,
  } = useResponsiveMenuPlacement(DEFAULT_MENU_MAX_HEIGHT);

  const normalizedOptions = Array.isArray(options) ? options : [];
  const calculateMenuPosition = React.useCallback(
    (event: React.SyntheticEvent) => {
      const selectElement = event.currentTarget as HTMLElement;
      if (!selectElement) return;

      const selectRect = selectElement.getBoundingClientRect();
      setMenuMinWidth(selectRect.width);
      updateMenuPlacement(selectElement);
    },
    [updateMenuPlacement],
  );

  const visibleOptions = React.useMemo(() => {
    const trimmed = searchText.trim().toLowerCase();
    if (trimmed.length > 0) {
      return normalizedOptions.filter((opt) =>
        String(opt.label || "")
          .toLowerCase()
          .includes(trimmed),
      );
    }
    return normalizedOptions.slice(0, Math.max(0, defaultLimit));
  }, [normalizedOptions, searchText, defaultLimit]);

  return (
    <Box
      className={`inputGroupWrapper flexCol ${
        isShowInColumn ? "isShowInColumn" : ""
      } ${extraClassName} ${isMobile ? "sp" : ""} ${
        hasSpacing === false ? "noSpacing" : ""
      }`}
      // AI 画面コンテキスト用 (getPageContext が拾う)
      data-field={name || undefined}
      data-label={label || undefined}
      data-options={
        options && options.length > 0 ? JSON.stringify(options) : undefined
      }
      sx={{
        display: "flex !important",
        flexDirection: "column !important",
        width: "100%",
      }}
    >
      <Typography
        className="inputLabel"
        sx={{
          width: { lg: labelWidth, xs: "100%" },
          whiteSpace: "nowrap",
          fontSize: "12px !important",
          alignSelf: "flex-start",
          pb: "4px",
          mb: "1px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
        {isRequired && (
          <span style={{ display: "flex", marginLeft: "4px" }}>
            <CustomRequireIcon />
          </span>
        )}
        {labelSuffix}
        {labelTooltip && (
          <Tooltip
            title={labelTooltip}
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: { fontSize: "11px", maxWidth: 280, lineHeight: 1.5 },
              },
            }}
          >
            <span
              style={{
                display: "flex",
                marginLeft: "4px",
                alignItems: "center",
              }}
            >
              <HelpOutlineIcon
                sx={{
                  fontSize: 14,
                  color: "#989898",
                }}
              />
            </span>
          </Tooltip>
        )}
      </Typography>
      <Box
        sx={{
          width: "100%",
          maxWidth: { sm: inputWidth, xs: inputWidthSp },
        }}
      >
        <FormControl
          fullWidth
          error={isRequired ? !!fieldError : false}
          disabled={disabled}
        >
          {control ? (
            <Controller
              name={name}
              control={control}
              rules={isRequired ? { required: `${label}は必須です` } : {}}
              render={({ field }) => {
                return (
                  <Select
                    {...field}
                    displayEmpty
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      field.onChange(nextValue);
                      if (onChangeValue) onChangeValue(nextValue);
                    }}
                    onOpen={calculateMenuPosition}
                    IconComponent={DropDownArrowIcon}
                    readOnly={readonly}
                    data-testid={`select-${name}`}
                    renderValue={(value) => {
                      const currentOption = normalizedOptions.find(
                        (opt) => opt.value === value,
                      );
                      const displayText = currentOption
                        ? currentOption.label
                        : placeholder || "";
                      return <span>{displayText}</span>;
                    }}
                    MenuProps={{
                      anchorOrigin: menuAnchorOrigin,
                      transformOrigin: menuTransformOrigin,
                      PaperProps: {
                        style: {
                          maxHeight: menuMaxHeight,
                          ...(autoMenuWidth
                            ? {
                                // 最長ラベルに合わせて広げる。トリガーより狭くはしない。
                                width: "max-content",
                                minWidth: menuMinWidth,
                                maxWidth: "calc(100vw - 32px)",
                              }
                            : {}),
                        },
                      },
                    }}
                    sx={{
                      height: { sm: "34px", xs: "32px" },
                      fontSize: "12px",
                      backgroundColor: readonly ? "#F5F5F5" : "#ffffff",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: fieldError ? "#d32f2f" : "#D9D9D9",
                        borderRadius: "8px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: fieldError ? "#d32f2f" : "#D9D9D9",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: fieldError ? "#d32f2f" : "#D9D9D9",
                        borderWidth: "1px",
                      },
                      "&:hover": {
                        backgroundColor: readonly ? "#F5F5F5" : "#ffffff",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#F5F5F5 !important",
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#D9D9D9 !important",
                          borderRadius: "8px",
                        },
                      },
                      ".MuiSelect-icon": {
                        fontSize: "14px",
                        display: readonly
                          ? "none"
                          : disabled
                            ? "none"
                            : "block",
                        top: "calc(50% + 0px)",
                        transform: "translateY(-50%)",
                        right: "12px !important",
                        marginRight: "0px !important",
                      },
                      "& .MuiSelect-iconOpen": {
                        transform: "translateY(-50%) rotate(180deg) !important",
                      },
                      "& .MuiSelect-select": {
                        color: field.value
                          ? readonly || disabled
                            ? "#3e3e3e"
                            : "inherit"
                          : "#989898",
                        fontSize: "12px",
                      },
                      "& .MuiSelect-select.Mui-disabled": {
                        color: "#3e3e3e !important",
                        WebkitTextFillColor: "#3e3e3e !important",
                      },
                    }}
                  >
                    {searchable && (
                      <ListSubheader
                        sx={{
                          pt: 0.5,
                          px: 1,
                          position: "sticky",
                          top: 0,
                          backgroundColor: "white",
                          zIndex: 1,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <TextField
                          autoFocus
                          fullWidth
                          placeholder={searchPlaceholder}
                          size="small"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: { xs: 32, sm: 34 },
                              borderRadius: "8px",
                              "& fieldset": {
                                borderColor: "#D9D9D9",
                              },
                              "&:hover fieldset": {
                                borderColor: "#D9D9D9",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#D9D9D9 !important",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              fontSize: { xs: 10, sm: 12 },
                              "&::placeholder": {
                                fontSize: { xs: 10, sm: 12 },
                                opacity: 0.8,
                                color: "#989898",
                              },
                            },
                          }}
                        />
                      </ListSubheader>
                    )}
                    {placeholder ? (
                      <MenuItem
                        value=""
                        sx={{
                          fontSize: "12px",
                          color: "#989898",
                          padding: "8px 16px",
                        }}
                      >
                        {placeholder}
                      </MenuItem>
                    ) : null}
                    {visibleOptions
                      .filter((option) => {
                        if (!option) return false;
                        if (typeof option !== "object") return false;
                        if (option.value === undefined || option.value === null)
                          return false;
                        return true;
                      })
                      .map((option, index) => {
                        if (
                          !option ||
                          option.value === undefined ||
                          option.value === null
                        ) {
                          return null;
                        }
                        return (
                          <MenuItem
                            key={`${option.value}-${index}`}
                            value={option.value}
                            sx={{
                              fontSize: "12px",
                              display: "block",
                              ...(autoMenuWidth
                                ? { minWidth: "max-content" }
                                : {}),
                            }}
                          >
                            {autoMenuWidth ? (
                              <Box sx={{ whiteSpace: "nowrap" }}>
                                {String(option.label || option.value)}
                              </Box>
                            ) : (
                              <CustomTruncateText
                                text={String(option.label || option.value)}
                              />
                            )}
                          </MenuItem>
                        );
                      })
                      .filter(Boolean)}
                    {(!options ||
                      !Array.isArray(options) ||
                      options.length === 0) &&
                      !placeholder && (
                        <MenuItem
                          value=""
                          sx={{
                            fontSize: "12px",
                            color: "#989898",
                            padding: "8px 16px",
                          }}
                        >
                          選択肢がありません
                        </MenuItem>
                      )}
                  </Select>
                );
              }}
            />
          ) : (
            <Select
              displayEmpty
              value=""
              disabled={disabled}
              readOnly={readonly}
              onOpen={calculateMenuPosition}
              IconComponent={DropDownArrowIcon}
              data-testid={`select-${name}`}
              MenuProps={{
                anchorOrigin: menuAnchorOrigin,
                transformOrigin: menuTransformOrigin,
                PaperProps: {
                  style: {
                    maxHeight: menuMaxHeight,
                    ...(autoMenuWidth
                      ? {
                          width: "max-content",
                          minWidth: menuMinWidth,
                          maxWidth: "calc(100vw - 32px)",
                        }
                      : {}),
                  },
                },
              }}
              sx={{
                height: { sm: "34px", xs: "32px" },
                fontSize: "12px",
                backgroundColor: readonly || disabled ? "#F5F5F5" : "#ffffff",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                  borderRadius: "8px",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#F5F5F5 !important",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D9D9D9 !important",
                    borderRadius: "8px",
                  },
                },
              }}
            >
              <MenuItem value="">
                {placeholder || "選択肢がありません"}
              </MenuItem>
            </Select>
          )}
          {isRequired && fieldError && (
            <FormHelperText
              sx={{
                marginTop: "4px",
                marginBottom: "0",
                marginLeft: "14px",
                marginRight: "14px",
              }}
            >
              {fieldError?.message}
            </FormHelperText>
          )}
        </FormControl>
      </Box>
    </Box>
  );
};

export default CustomSingleSelect;
